import React from "react";
import type { CallbackEvent } from "@shopify/polaris-types";
import {
  CollapsibleSectionCard,
  SectionHeader,
  ColorRow,
} from "../../../components/shared/EditorShared";
import type { QuantityTiersEditorState } from "../../../store/quantity-tiers-editor.mapping";

// ─── Layout template selector ─────────────────────────────────────────────────

type Layout = QuantityTiersEditorState["layout"];

function LayoutCard({
  selected,
  label,
  onSelect,
  preview,
}: {
  selected: boolean;
  label: string;
  onSelect: () => void;
  preview: React.ReactNode;
}) {
  return (
    <button
      onClick={onSelect}
      className={`relative flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all text-sm font-medium ${
        selected
          ? "border-indigo-500 bg-indigo-50 text-indigo-700"
          : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
      }`}
    >
      {selected && (
        <span className="absolute top-2 right-2 w-4 h-4 bg-indigo-500 rounded-full flex items-center justify-center">
          <svg
            className="w-2.5 h-2.5 text-white"
            fill="currentColor"
            viewBox="0 0 12 12"
          >
            <path
              d="M3.5 7.5 L5.5 9.5 L8.5 4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      )}
      <div className="w-full h-12 flex items-center justify-center text-gray-400">
        {preview}
      </div>
      <span>{label}</span>
    </button>
  );
}

// Simple SVG icons representing each layout
function HorizontalLayoutIcon() {
  return (
    <svg viewBox="0 0 48 32" className="w-10 h-7" fill="none">
      <rect
        x="1"
        y="1"
        width="46"
        height="13"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="rgba(99,102,241,0.08)"
      />
      <rect
        x="1"
        y="18"
        width="46"
        height="13"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="rgba(99,102,241,0.08)"
      />
    </svg>
  );
}

function GridLayoutIcon() {
  return (
    <svg viewBox="0 0 48 32" className="w-10 h-7" fill="none">
      <rect
        x="1"
        y="1"
        width="21"
        height="30"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="rgba(99,102,241,0.08)"
      />
      <rect
        x="26"
        y="1"
        width="21"
        height="30"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="rgba(99,102,241,0.08)"
      />
    </svg>
  );
}

// ─── Behavior checkbox ────────────────────────────────────────────────────────

function BehaviorCheckbox({
  checked,
  label,
  description,
  onChange,
}: {
  checked: boolean;
  label: string;
  description?: string;
  onChange: (v: boolean) => void;
}) {
  return (
    <s-stack direction="block" gap="small-100">
      <s-checkbox
        label={label}
        checked={checked}
        onChange={(e: CallbackEvent<"s-checkbox">) =>
          onChange(e.currentTarget.checked)
        }
      />
      {description && <s-text color="subdued">{description}</s-text>}
    </s-stack>
  );
}

// ─── Section 3: Design ────────────────────────────────────────────────────────

export function DesignSection({
  layout,
  backgroundColor,
  selectedBackgroundColor,
  activeBorderColor,
  borderRadius,
  hideProductImage,
  hideVariantQuantitySelectors,
  disableVariantSelectors,
  useCompareAtPriceAsBase,
  disableIfExceedsMaxQuantity,
  onLayoutChange,
  onBackgroundColorChange,
  onSelectedBackgroundColorChange,
  onActiveBorderColorChange,
  onBorderRadiusChange,
  onHideProductImageChange,
  onHideVariantQuantitySelectorsChange,
  onDisableVariantSelectorsChange,
  onUseCompareAtPriceAsBaseChange,
  onDisableIfExceedsMaxQuantityChange,
}: {
  layout: Layout;
  backgroundColor: string;
  selectedBackgroundColor: string;
  activeBorderColor: string;
  borderRadius: number;
  hideProductImage: boolean;
  hideVariantQuantitySelectors: boolean;
  disableVariantSelectors: boolean;
  useCompareAtPriceAsBase: boolean;
  disableIfExceedsMaxQuantity: boolean;
  onLayoutChange: (v: Layout) => void;
  onBackgroundColorChange: (v: string) => void;
  onSelectedBackgroundColorChange: (v: string) => void;
  onActiveBorderColorChange: (v: string) => void;
  onBorderRadiusChange: (v: number) => void;
  onHideProductImageChange: (v: boolean) => void;
  onHideVariantQuantitySelectorsChange: (v: boolean) => void;
  onDisableVariantSelectorsChange: (v: boolean) => void;
  onUseCompareAtPriceAsBaseChange: (v: boolean) => void;
  onDisableIfExceedsMaxQuantityChange: (v: boolean) => void;
}) {
  return (
    <CollapsibleSectionCard
      header={
        <SectionHeader
          number={3}
          title="Personaliza el diseño y comportamiento"
        />
      }
    >
      <s-stack direction="block" gap="base">
        {/* Layout selector */}
        <s-stack direction="block" gap="small-200">
          <s-text type="strong">Seleccionar modo</s-text>
          <div className="grid grid-cols-2 gap-3">
            <LayoutCard
              selected={layout === "horizontal"}
              label="Clásico"
              onSelect={() => onLayoutChange("horizontal")}
              preview={<HorizontalLayoutIcon />}
            />
            <LayoutCard
              selected={layout === "grid"}
              label="Moderno"
              onSelect={() => onLayoutChange("grid")}
              preview={<GridLayoutIcon />}
            />
          </div>
        </s-stack>

        <div className="h-px bg-gray-100" />

        {/* Colors */}
        <ColorRow
          label="Color del fondo"
          value={backgroundColor}
          onChange={onBackgroundColorChange}
        />
        <ColorRow
          label="Color del fondo seleccionado"
          value={selectedBackgroundColor}
          onChange={onSelectedBackgroundColorChange}
        />
        <ColorRow
          label="Color del borde activo"
          value={activeBorderColor}
          onChange={onActiveBorderColorChange}
        />

        <div className="h-px bg-gray-100" />

        {/* Border radius */}
        <s-stack direction="block" gap="small-200">
          <s-text>Border Radius</s-text>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={24}
              value={borderRadius}
              onChange={(e) => onBorderRadiusChange(Number(e.target.value))}
              className="flex-1 accent-indigo-600"
            />
            <span className="text-sm text-gray-600 w-10 text-right tabular-nums">
              {borderRadius}px
            </span>
          </div>
        </s-stack>

        <div className="h-px bg-gray-100" />

        {/* Behavior toggles */}
        <s-stack direction="block" gap="base">
          <s-text type="strong">Comportamiento</s-text>
          <BehaviorCheckbox
            checked={hideProductImage}
            label="Ocultar la imagen del producto"
            onChange={onHideProductImageChange}
          />
          <BehaviorCheckbox
            checked={hideVariantQuantitySelectors}
            label="Ocultar los números junto a los selectores de variantes"
            onChange={onHideVariantQuantitySelectorsChange}
          />
          <BehaviorCheckbox
            checked={disableVariantSelectors}
            label="Deshabilitar los selectores de variantes"
            description="Si el producto tiene variantes, los selectores no estarán disponibles dentro de la oferta."
            onChange={onDisableVariantSelectorsChange}
          />
          <BehaviorCheckbox
            checked={useCompareAtPriceAsBase}
            label="Usar el precio de comparación como precio anterior"
            description="El precio tachado en cada oferta mostrará el precio de comparación del producto. Los descuentos siguen calculándose sobre el precio real."
            onChange={onUseCompareAtPriceAsBaseChange}
          />
          <BehaviorCheckbox
            checked={disableIfExceedsMaxQuantity}
            label="Deshabilitar esta oferta si el cliente selecciona una cantidad mayor a tus ofertas"
            description="Si el cliente elige más unidades de las que cubre tu oferta máxima, la oferta se ocultará automáticamente."
            onChange={onDisableIfExceedsMaxQuantityChange}
          />
        </s-stack>
      </s-stack>
    </CollapsibleSectionCard>
  );
}
